import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { CABLE_NAMES } from '../../data/cableProducts.js';
import { API } from '../../config/api';

// stranding calc keeps the full list incl. COPPER_ROPE (it strands them)
const CABLE_NAME_OPTIONS = CABLE_NAMES;

const round = (n, d = 2) => {
  const f = Math.pow(10, d);
  return Math.round(n * f) / f;
};

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Stranding / Rope take-up factor calculation.
 *
 * When conductors are twisted helically around a central axis,
 * the conductor path is longer than the resulting rope.
 *
 * lay length (pas) = distance along rope axis for one full revolution
 * D = pitch circle diameter (≈ rope diameter for the conductor layer)
 * helix angle α = atan(π × D / pas)
 * take-up factor = 1 / cos(α) = √(1 + (π × D / pas)²)
 *
 * L_conductor_needed = L_rope_desired × take-up factor
 * L_rope_result = L_conductor_available / take-up factor
 */
const calcTakeUpFactor = (ropeDiameter, layLength) => {
  const D = toNum(ropeDiameter);
  const pas = toNum(layLength);
  if (D <= 0 || pas <= 0) return null;

  const ratio = (Math.PI * D) / pas;
  const factor = Math.sqrt(1 + ratio * ratio);
  const helixAngleDeg = (Math.atan(ratio) * 180) / Math.PI;

  return { factor, helixAngleDeg, ratio };
};

const StrandingCalculator = () => {
  // ── product search ──
  const [product, setProduct] = useState({
    name: '',
    numberOfConductors: '',
    section: '',
    color: '',
    hasMixedConductorSections: false,
    greenYellowSection: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [standard, setStandard] = useState(null);

  // ── calculator inputs ──
  const [selectedPhaseKey, setSelectedPhaseKey] = useState('');
  const [layLength, setLayLength] = useState('');
  const [desiredRopeLength, setDesiredRopeLength] = useState('');

  const handleChange = (field, value) => {
    setProduct(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'hasMixedConductorSections' && !value
        ? { greenYellowSection: '' }
        : {})
    }));
  };

  const isSearchValid =
    product.name.trim() !== '' &&
    product.numberOfConductors !== '' &&
    product.section !== '' &&
    product.color.trim() !== '' &&
    (!product.hasMixedConductorSections || product.greenYellowSection !== '');

  const fetchStandard = async () => {
    try {
      setLoading(true);
      setError('');
      setStandard(null);
      setSelectedPhaseKey('');

      const payload = [
        {
          name: product.name,
          numberOfConductors: Number(product.numberOfConductors),
          section: Number(product.section),
          color: product.color,
          hasMixedConductorSections: Boolean(product.hasMixedConductorSections),
          greenYellowSection: product.hasMixedConductorSections
            ? Number(product.greenYellowSection)
            : null
        }
      ];

      const response = await axios.post(
        `${API.BACKEND}/api/v1/standard/find-many`,
        payload
      );

      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

      const firstStandard = data?.[0] || null;
      setStandard(firstStandard);

      // auto-select first applicable phase
      const phases = extractStrandingPhases(firstStandard);
      if (phases.length) setSelectedPhaseKey(phases[0].key);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Failed to load standard.');
    } finally {
      setLoading(false);
    }
  };

  // ── extract phases that involve stranding ──
  const extractStrandingPhases = std => {
    if (!std?.phases) return [];
    const result = [];

    for (let i = 0; i < std.phases.length; i++) {
      const phase = std.phases[i];

      if (phase.name === 'rope' && phase.diameter) {
        result.push({
          key: `rope-${i}`,
          name: 'rope',
          label: 'Rope (Infuniere)',
          diameter: toNum(phase.diameter),
          numberOfConductors: Number(std.numberOfConductors) || 0,
          phase
        });
      }

      if (phase.name === 'pre_stranding' && Array.isArray(phase.rope)) {
        phase.rope.forEach((rope, ri) => {
          if (rope.diameter) {
            result.push({
              key: `pre_stranding-${i}-rope${ri}`,
              name: 'pre_stranding',
              label: `Pre-Stranding — Rope #${ri + 1} (Ø${rope.diameter}mm)`,
              diameter: toNum(rope.diameter),
              numberOfConductors: null, // determined by numberOfWires
              wireInfo: rope,
              phase
            });
          }
        });
      }
    }

    return result;
  };

  const phaseOptions = useMemo(() => extractStrandingPhases(standard), [standard]);

  const selectedPhase = useMemo(
    () => phaseOptions.find(p => p.key === selectedPhaseKey) || null,
    [phaseOptions, selectedPhaseKey]
  );

  // ── calculation ──
  const calculation = useMemo(() => {
    if (!selectedPhase) return null;

    const D = selectedPhase.diameter;
    const pas = toNum(layLength);
    if (D <= 0 || pas <= 0) return null;

    const result = calcTakeUpFactor(D, pas);
    if (!result) return null;

    const desiredLen = toNum(desiredRopeLength);

    // how much conductor you need per rope meter
    const conductorPerRopeMeter = result.factor;

    // if desired rope length specified, compute total conductor needed
    const conductorNeeded = desiredLen > 0 ? round(desiredLen * result.factor, 2) : null;
    const ropeFromConductor = desiredLen > 0 ? round(desiredLen / result.factor, 2) : null;

    // wire info for pre_stranding
    let wireBreakdown = null;
    if (selectedPhase.wireInfo) {
      const w = selectedPhase.wireInfo;
      const diams = Array.isArray(w.wireDiameter) ? w.wireDiameter : [];
      const counts = Array.isArray(w.numberOfWires) ? w.numberOfWires : [];

      wireBreakdown = diams.map((d, i) => ({
        wireDiameter: d,
        count: counts[i] || 0,
        totalWires: counts[i] || 0,
        perWireLength: conductorNeeded,
        totalLength: conductorNeeded ? round(conductorNeeded * (counts[i] || 1), 2) : null
      }));
    }

    // rope phase: multiply by number of conductors
    let conductorBreakdown = null;
    if (selectedPhase.name === 'rope' && selectedPhase.numberOfConductors > 0) {
      const nCond = selectedPhase.numberOfConductors;
      conductorBreakdown = {
        numberOfConductors: nCond,
        lengthPerConductor: conductorNeeded,
        totalConductorLength: conductorNeeded ? round(conductorNeeded * nCond, 2) : null
      };
    }

    return {
      ropeDiameter: D,
      layLength: pas,
      factor: round(result.factor, 6),
      helixAngleDeg: round(result.helixAngleDeg, 2),
      conductorPerRopeMeter: round(conductorPerRopeMeter, 4),
      wastePct: round((result.factor - 1) * 100, 2),
      conductorNeeded,
      ropeFromConductor,
      wireBreakdown,
      conductorBreakdown
    };
  }, [selectedPhase, layLength, desiredRopeLength]);

  return (
    <div className="mb-4">
      <h1 className="fw-semibold mb-3">Stranding / Rope Calculator</h1>

      {/* ── Search Standard ── */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="fw-semibold">Search Standard</div>
          <Form.Check
            type="switch"
            label="Mixed sections (GY)"
            checked={product.hasMixedConductorSections}
            onChange={e => handleChange('hasMixedConductorSections', e.target.checked)}
          />
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Label>Cable Name *</Form.Label>
              <Form.Select value={product.name} onChange={e => handleChange('name', e.target.value)}>
                <option value="">Select</option>
                {CABLE_NAME_OPTIONS.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Conductors *</Form.Label>
              <Form.Control type="number" value={product.numberOfConductors} onChange={e => handleChange('numberOfConductors', e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label>Section (mm2) *</Form.Label>
              <Form.Control type="number" value={product.section} onChange={e => handleChange('section', e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label>Color *</Form.Label>
              <Form.Select value={product.color} onChange={e => handleChange('color', e.target.value)}>
                <option value="">Select</option>
                <option value="BLACK">BLACK</option>
                <option value="GREY">GREY</option>
                <option value="WHITE">WHITE</option>
              </Form.Select>
            </Col>
            {product.hasMixedConductorSections && (
              <Col md={2}>
                <Form.Label>GY Section</Form.Label>
                <Form.Control type="number" value={product.greenYellowSection} onChange={e => handleChange('greenYellowSection', e.target.value)} />
              </Col>
            )}
            <Col md={1} className="d-flex align-items-end">
              <Button variant="primary" disabled={!isSearchValid || loading} onClick={fetchStandard}>
                {loading ? <Spinner animation="border" size="sm" /> : 'Search'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* ── Calculator ── */}
      {standard && (
        <Card className="shadow-sm">
          <Card.Header className="fw-semibold">
            {standard.name} {standard.numberOfConductors}x{standard.section}
            {standard.hasMixedConductorSections && ` + GY ${standard.greenYellowSection}`}
            <Badge bg="primary" className="ms-2">{standard.color}</Badge>
          </Card.Header>
          <Card.Body>
            {!phaseOptions.length ? (
              <Alert variant="warning">
                This standard has no rope or pre_stranding phase with a defined diameter.
              </Alert>
            ) : (
              <>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Label>Select Phase</Form.Label>
                    <Form.Select
                      value={selectedPhaseKey}
                      onChange={e => setSelectedPhaseKey(e.target.value)}
                    >
                      {phaseOptions.map(p => (
                        <option key={p.key} value={p.key}>
                          {p.label} — Ø{p.diameter} mm
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Lay Length / Step (mm) *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={layLength}
                      onChange={e => setLayLength(e.target.value)}
                      placeholder="e.g. 120"
                    />
                    <Form.Text className="text-muted">
                      Distance for one complete revolution
                    </Form.Text>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Desired Rope Length (m)</Form.Label>
                    <Form.Control
                      type="number"
                      value={desiredRopeLength}
                      onChange={e => setDesiredRopeLength(e.target.value)}
                      placeholder="e.g. 5000"
                    />
                    <Form.Text className="text-muted">
                      How much rope/strand you want to produce
                    </Form.Text>
                  </Col>
                </Row>

                {selectedPhase && (
                  <Alert variant="info" className="mb-3">
                    <strong>Formula:</strong> L<sub>conductor</sub> = L<sub>rope</sub> × √(1 + (π × D / pas)²)
                    {selectedPhase.diameter > 0 && layLength > 0 && (
                      <span className="ms-2">
                        = L<sub>rope</sub> × √(1 + (π × {selectedPhase.diameter} / {layLength})²)
                      </span>
                    )}
                  </Alert>
                )}

                {calculation && (
                  <>
                    <Table bordered responsive className="mb-3">
                      <tbody>
                        <tr>
                          <th style={{ width: '40%' }}>Rope/Strand Diameter (D)</th>
                          <td><b>{calculation.ropeDiameter} mm</b></td>
                        </tr>
                        <tr>
                          <th>Lay Length / Step (pas)</th>
                          <td><b>{calculation.layLength} mm</b></td>
                        </tr>
                        <tr>
                          <th>Helix Angle (α)</th>
                          <td>{calculation.helixAngleDeg}°</td>
                        </tr>
                        <tr className="table-warning">
                          <th>Take-Up Factor</th>
                          <td><b>{calculation.factor}</b></td>
                        </tr>
                        <tr>
                          <th>Conductor per Rope Meter</th>
                          <td>{calculation.conductorPerRopeMeter} m conductor → 1 m rope</td>
                        </tr>
                        <tr>
                          <th>Extra Length (%)</th>
                          <td>{calculation.wastePct}%</td>
                        </tr>
                      </tbody>
                    </Table>

                    {calculation.conductorNeeded && (
                      <>
                        <h5 className="mt-4">Production Planning</h5>
                        <Table bordered responsive>
                          <tbody>
                            <tr className="table-success">
                              <th style={{ width: '40%' }}>Desired Rope Length</th>
                              <td><b>{toNum(desiredRopeLength).toLocaleString()} m</b></td>
                            </tr>
                            <tr className="table-info">
                              <th>Conductor Length Needed (per strand)</th>
                              <td><b>{calculation.conductorNeeded.toLocaleString()} m</b></td>
                            </tr>
                            {calculation.ropeFromConductor && (
                              <tr>
                                <th>Rope Output from {toNum(desiredRopeLength).toLocaleString()} m conductor</th>
                                <td>{calculation.ropeFromConductor.toLocaleString()} m</td>
                              </tr>
                            )}
                          </tbody>
                        </Table>

                        {/* Conductor breakdown for rope phase */}
                        {calculation.conductorBreakdown && (
                          <Card className="mt-3 border-primary-subtle">
                            <Card.Header className="py-2 fw-semibold">
                              Conductor Input Requirements
                            </Card.Header>
                            <Card.Body className="py-2">
                              <Table bordered size="sm" className="mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>Item</th>
                                    <th className="text-end">Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td>Number of conductors</td>
                                    <td className="text-end">{calculation.conductorBreakdown.numberOfConductors}</td>
                                  </tr>
                                  <tr>
                                    <td>Length per conductor</td>
                                    <td className="text-end fw-bold">{calculation.conductorBreakdown.lengthPerConductor?.toLocaleString()} m</td>
                                  </tr>
                                  <tr className="table-warning">
                                    <td>Total conductor input</td>
                                    <td className="text-end fw-bold">{calculation.conductorBreakdown.totalConductorLength?.toLocaleString()} m</td>
                                  </tr>
                                </tbody>
                              </Table>
                            </Card.Body>
                          </Card>
                        )}

                        {/* Wire breakdown for pre_stranding */}
                        {calculation.wireBreakdown && (
                          <Card className="mt-3 border-info-subtle">
                            <Card.Header className="py-2 fw-semibold">
                              Wire Input Requirements
                            </Card.Header>
                            <Card.Body className="py-2">
                              <Table bordered size="sm" className="mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>Wire Ø (mm)</th>
                                    <th className="text-end">Count</th>
                                    <th className="text-end">Length per Wire (m)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {calculation.wireBreakdown.map((w, i) => (
                                    <tr key={i}>
                                      <td>Ø {w.wireDiameter} mm</td>
                                      <td className="text-end">{w.count}</td>
                                      <td className="text-end fw-bold">{w.perWireLength?.toLocaleString()} m</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </Card.Body>
                          </Card>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default StrandingCalculator;
